// The panels behind the Week, Month, Quarter and Year tabs. Each shows the
// periods of its kind around the one picked and hands back the one clicked
// as `{ startDate, endDate }`; a period that has not begun is shown but not
// offered. `v`: the dropdown's variant styles; `locale`: moment's, for the
// names of the days and months.
import React, { useMemo, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import moment from "moment"
import { periodOf } from "./periods"

const today = () => moment().endOf("day")
const localized = (date, locale) => (locale ? moment(date).locale(locale) : moment(date))
const sameStart = (range, period) => !!range?.startDate && moment(range.startDate).isSame(period.startDate, "day")

/** A title between two arrows; the forward arrow stops at the period now. */
function Header({ v, title, onPrev, onNext, nextDisabled }) {
  return (
    <div className={v.panel_header}>
      <button type="button" className={v.panel_nav} onClick={onPrev} aria-label="previous" data-testid="period-panel-prev">
        <ChevronLeftIcon className="w-5 h-5" />
      </button>
      <span className={v.panel_title}>{title}</span>
      <button type="button" className={twMerge(v.panel_nav, nextDisabled && "invisible")} onClick={onNext} disabled={nextDisabled} aria-label="next" data-testid="period-panel-next">
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  )
}

/** The days of a month as weeks, Monday first, padded with the days around it. */
function weeksOf(month) {
  const first = moment(month).startOf("month").startOf("isoWeek")
  const last = moment(month).endOf("month").endOf("isoWeek")
  const weeks = []
  for (const day = first.clone(); day.isSameOrBefore(last, "day"); ) {
    const week = []
    for (let i = 0; i < 7; i++, day.add(1, "day")) week.push(day.clone())
    weeks.push(week)
  }
  return weeks
}

/** Weekday initials from Monday, in the locale. */
const weekdaysOf = (locale) => {
  const names = (locale ? moment.localeData(locale) : moment.localeData()).weekdaysMin()
  return [...names.slice(1), names[0]]
}

/** A month to pick a week from: a click on any day picks its week. */
export function WeekPanel({ v, value, onPick, locale }) {
  const [month, setMonth] = useState(() => moment(value?.startDate || undefined).startOf("month"))
  const [hovered, setHovered] = useState(null)
  const weeks = useMemo(() => weeksOf(month), [month])
  const now = today()
  return (
    <div data-testid="period-panel-week">
      <Header
        v={v}
        title={localized(month, locale).format("MMM YYYY")}
        onPrev={() => setMonth(month.clone().subtract(1, "month"))}
        onNext={() => setMonth(month.clone().add(1, "month"))}
        nextDisabled={month.clone().add(1, "month").isAfter(now)}
      />
      <div className={v.week_grid}>
        {weekdaysOf(locale).map((name, i) => (
          <span key={name} className={twMerge(v.week_head, i > 4 && v.weekend)}>
            {name}
          </span>
        ))}
        {weeks.map((week) => {
          const period = periodOf("week", week[0])
          const key = week[0].format("YYYY-MM-DD")
          const disabled = moment(period.startDate).isAfter(now)
          const selected = sameStart(value, period)
          return week.map((day, i) => {
            const future = day.isAfter(now)
            return (
              <button
                key={day.format("YYYY-MM-DD")}
                type="button"
                disabled={disabled}
                onClick={() => onPick(period)}
                onMouseEnter={() => setHovered(key)}
                onMouseLeave={() => setHovered(null)}
                className={twMerge(
                  v.week_day,
                  i > 4 && v.weekend,
                  !day.isSame(month, "month") && v.cell_muted,
                  future && v.cell_disabled,
                  !disabled && hovered === key && v.cell_hover,
                  selected && v.cell_selected
                )}
                data-testid={i === 0 ? "period-week" : undefined}
              >
                {day.date()}
              </button>
            )
          })
        })}
      </div>
    </div>
  )
}

/** A year of small month calendars: a click on a month picks it. */
export function MonthPanel({ v, value, onPick, locale }) {
  const [year, setYear] = useState(() => moment(value?.startDate || undefined).year())
  const now = today()
  const months = Array.from({ length: 12 }, (_, i) => moment({ year, month: i, day: 1 }))
  return (
    <div data-testid="period-panel-month">
      <Header v={v} title={String(year)} onPrev={() => setYear(year - 1)} onNext={() => setYear(year + 1)} nextDisabled={year + 1 > now.year()} />
      <div className={v.month_grid}>
        {months.map((month) => {
          const period = periodOf("month", month)
          const disabled = month.isAfter(now)
          return (
            <button
              key={month.month()}
              type="button"
              disabled={disabled}
              onClick={() => onPick(period)}
              className={twMerge(v.month_cell, disabled ? v.cell_disabled : v.cell_hover_able, sameStart(value, period) && v.cell_selected)}
              data-testid="period-month"
            >
              <span className={v.month_name}>{localized(month, locale).format("MMM")}</span>
              <span className={v.mini_grid} aria-hidden>
                {weeksOf(month).flat().map((day) => (
                  <span key={day.format("MM-DD")} className={day.isSame(month, "month") ? "" : "invisible"}>
                    {day.date()}
                  </span>
                ))}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** A year as four quarters, each with its months: a click on the row picks the quarter. */
export function QuarterPanel({ v, value, onPick, locale }) {
  const [year, setYear] = useState(() => moment(value?.startDate || undefined).year())
  const now = today()
  return (
    <div data-testid="period-panel-quarter">
      <Header v={v} title={String(year)} onPrev={() => setYear(year - 1)} onNext={() => setYear(year + 1)} nextDisabled={year + 1 > now.year()} />
      <div className="flex flex-col gap-1">
        {[1, 2, 3, 4].map((q) => {
          const start = moment({ year, month: (q - 1) * 3, day: 1 })
          const period = periodOf("quarter", start)
          const disabled = start.isAfter(now)
          return (
            <button
              key={q}
              type="button"
              disabled={disabled}
              onClick={() => onPick(period)}
              className={twMerge(v.quarter_row, disabled ? v.cell_disabled : v.cell_hover_able, sameStart(value, period) && v.cell_selected)}
              data-testid="period-quarter"
            >
              <span>Q{q}</span>
              {[0, 1, 2].map((i) => (
                <span key={i}>{localized(start.clone().add(i, "month"), locale).format("MMM")}</span>
              ))}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** The years from now back to `minYear`, newest first. */
export function YearPanel({ v, value, onPick, minYear }) {
  const current = moment().year()
  const picked = value?.startDate ? moment(value.startDate).year() : null
  const years = Array.from({ length: Math.max(1, current - minYear + 1) }, (_, i) => current - i)
  return (
    <div className={v.year_list} data-testid="period-panel-year">
      {years.map((year) => (
        <button
          key={year}
          type="button"
          onClick={() => onPick(periodOf("year", moment({ year, month: 0, day: 1 })))}
          className={twMerge(v.year_item, v.cell_hover_able, picked === year && v.cell_selected)}
          data-testid="period-year"
        >
          {year}
        </button>
      ))}
    </div>
  )
}
